import { useEffect, useMemo, useRef, useState } from 'react';
import { formatSeconds, type Task } from '@machs/shared';
import { useSync } from '../lib/sync';
import { usePlan } from '../lib/plan';
import { Icon, type IconName } from '../lib/icons';
import { Dialog, useToast } from './ui';
import { kindOf, mediaMeta } from './Composer';

const ICON: Record<ReturnType<typeof kindOf>, IconName> = { bild: 'image', video: 'video', sprache: 'wave', datei: 'file' };

function groesse(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Dateien, die in eine Aufgabe gezogen wurden: vor dem Ablegen lässt sich ein
 * Text oder eine Sprachnachricht dazulegen — oder man legt sie direkt ab.
 * Der Text steht an der ersten Datei, die Sprachnachricht antwortet auf sie,
 * damit der Zusammenhang im Verlauf sichtbar bleibt.
 */
export function DropDialog({ task, files: start, onClose }: { task: Task; files: File[]; onClose: () => void }) {
  const { client } = useSync();
  const plan = usePlan();
  const toast = useToast();
  const [files, setFiles] = useState(start);
  const [text, setText] = useState('');
  const [voice, setVoice] = useState<{ blob: Blob; mime: string; durationSec: number } | null>(null);
  const [recording, setRecording] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);

  // Vorschaubilder nur für Fotos, und nur solange der Dialog offen ist
  const previews = useMemo(() => files.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null)), [files]);
  useEffect(() => () => previews.forEach((u) => u && URL.revokeObjectURL(u)), [previews]);

  useEffect(() => {
    if (recording == null) return;
    const t = setInterval(() => setElapsed((Date.now() - recording) / 1000), 250);
    return () => clearInterval(t);
  }, [recording]);

  async function aufnehmen() {
    if (recorder.current) return recorder.current.stop();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm'].find((t) => MediaRecorder.isTypeSupported(t));
      const rec = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks: Blob[] = [];
      const since = Date.now();
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        recorder.current = null;
        setRecording(null);
        setElapsed(0);
        const durationSec = (Date.now() - since) / 1000;
        if (durationSec < 0.8) return toast('Zu kurz');
        const mime = rec.mimeType || 'audio/webm';
        setVoice({ blob: new Blob(chunks, { type: mime }), mime, durationSec });
      };
      rec.start(1000);
      recorder.current = rec;
      setRecording(since);
    } catch {
      toast('Kein Zugriff auf das Mikrofon');
    }
  }

  async function ablegen() {
    if (!files.length || busy) return;
    // Alle Dateien zusammen gegen den Speicher, Videos einzeln gegen die Länge
    const metas = await Promise.all(files.map((f) => mediaMeta(f)));
    const longest = Math.max(0, ...files.map((f, i) => (kindOf(f) === 'video' ? (metas[i]!.durationSec ?? 0) : 0)));
    if (!plan.allowUpload(files.reduce((n, f) => n + f.size, 0) + (voice?.blob.size ?? 0), longest || null)) return;
    recorder.current?.stop();
    setBusy(true);
    try {
      let first: string | null = null;
      for (const [i, file] of files.entries()) {
        const kind = kindOf(file);
        const meta = await mediaMeta(file);
        const id = await client.sendMessage({
          taskId: task.id,
          kind,
          body: i === 0 ? text.trim() : '',
          upload: { kind, file, fileName: file.name, mime: file.type || null, url: null, ...meta },
        });
        first ??= id;
      }
      if (voice && first) {
        await client.sendMessage({
          taskId: task.id,
          kind: 'sprache',
          replyToId: first,
          upload: { kind: 'sprache', file: voice.blob, fileName: `sprachnachricht.${voice.mime.includes('mp4') ? 'm4a' : 'webm'}`, mime: voice.mime, url: null, durationSec: voice.durationSec, width: null, height: null },
        });
      }
      onClose();
    } finally {
      setBusy(false);
    }
  }

  const leer = !text.trim() && !voice;
  const titel = files.length === 1 ? 'Datei ablegen' : `${files.length} Dateien ablegen`;

  return (
    <Dialog title={titel} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <ul className="droplist">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              {previews[i] ? <img src={previews[i]!} alt="" /> : <span className="dropicon"><Icon name={ICON[kindOf(f)]} size={18} /></span>}
              <span className="min-w-0 flex-1">
                <b className="block truncate text-[14px] font-medium">{f.name}</b>
                <small className="text-[12px] text-[var(--muted)]">{groesse(f.size)}</small>
              </span>
              <button className="iconbtn plain" style={{ width: 30, height: 30 }} onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`${f.name} entfernen`} data-tip="Entfernen">
                <Icon name="x" size={15} />
              </button>
            </li>
          ))}
        </ul>

        <label className="field">
          Dazu schreiben (optional)
          <textarea
            className="input"
            style={{ height: 84, paddingTop: 10, resize: 'none' }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Worum geht es bei diesen Dateien?"
            onKeyDown={(e) => e.key === 'Enter' && (e.metaKey || e.ctrlKey) && void ablegen()}
          />
        </label>

        <div className="flex items-center gap-2">
          {voice ? (
            <span className="chip" aria-pressed="true">
              <Icon name="wave" size={14} />Sprachnachricht {formatSeconds(voice.durationSec)}
              <button onClick={() => setVoice(null)} aria-label="Sprachnachricht verwerfen"><Icon name="x" size={13} /></button>
            </span>
          ) : (
            <button className="chip" aria-pressed={recording != null} onClick={() => void aufnehmen()}>
              <Icon name={recording != null ? 'stop' : 'mic'} size={14} />
              {recording != null ? `Aufnahme beenden ${formatSeconds(elapsed)}` : 'Sprachnachricht dazu'}
            </button>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" onClick={() => void ablegen()} disabled={!files.length || busy || recording != null}>
            {busy ? 'Wird abgelegt …' : leer ? 'Ohne Nachricht ablegen' : 'Ablegen'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
